/* eslint-disable camelcase -- these keys arrive from the API in snake_case */
/* eslint-disable react/jsx-props-no-spreading -- wrappers here forward props wholesale */
import { ui, when } from '@owenscorning/pcb.alpha';
import _ from 'lodash';
import mime from 'mime-types';
import { useCallback } from 'react';

import Content from './Content';
import { wrapRef } from '../../../../data';
import MediaObject from '../../../OC/PageBuilder/MediaObject';
import FileBadge from '../../../OC/oc-file-badge';
import { toast } from '../../../PageBuilder/helpers/toast';
import { connectLocation } from '../../../location/LocationConnectors';

const available_types = () => {
  const common_types = {
    upload: 'Upload',
    external: 'External (URL)',
  };

  if (Board.build.site === 'www.owenscorning.com') {
    return {
      dms: 'DMS',
      ...common_types,
    };
  }

  return common_types;
};

const Document = Content('Document', UI => ({
  version: '0.1',
  showShare: false,
  displayName: 'Document',
  paths: ['documents/uploads'],
  read: ({ contents, metadata }) => {
    let resolved = Array.isArray(contents) ? {} : contents;
    if (resolved?.type === 'external' && typeof resolved.external === 'undefined') {
      resolved = _.cloneDeep(resolved);
      resolved.external = {
        url: resolved.external_url,
      };
    }
    return { contents: resolved, metadata };
  },
  sidebar: {
    builder: ui`Form`.of({
      type: ui`Choices`.of(available_types)({
        label: 'Type',
        default: 'upload',
      }),
      document: [
        ({ UI: FieldUI, Board, path, value, onChange, ...props }) => {
          const onUploaded = useCallback(
            val => {
              if (!val) {
                Board.Change(val, _.concat(_.initial(path), 'thumbnail'));
              } else {
                const { url } = val;
                const thumbUrl = url.replace(/\/file$/, '/thumb');
                fetch(thumbUrl, { method: 'HEAD' }).then(response => {
                  if (response.status === 404) {
                    toast.warn('Thumbnail not available, please upload an image for thumbnail.');
                  } else {
                    Board.Change(thumbUrl, _.concat(_.initial(path), 'thumbnail'));
                  }
                });
              }
              onChange(val);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate trigger list
            [onChange]
          );
          return (
            <FieldUI.Upload.Document
              UI={FieldUI}
              Board={Board}
              path={path}
              value={value}
              onChange={onUploaded}
              {...props}
              detailed
            />
          );
        },
        {
          label: 'Upload',
          visible: when`../type`.is.equal.to('upload'),
        },
      ],
      dms: ui`Form`.of(ui`Data/Parameters`.of('BuilderDocument')({ dataset: 'documents', unwrapped: true }))({
        label: 'Select Details',
        visible: when`../type`.is.equal.to('dms'),
      }),
      external: ui`Form`.of({
        url: [
          ({ UI: FieldUI, Board, path, value, onChange, ...props }) => {
            const onChanged = useCallback(
              val => {
                Board.Change(val.mime_type, _.concat(_.initial(path), 'mime_type'));
                Board.Change(val.size_bytes, _.concat(_.initial(path), 'size_bytes'));
                onChange(val.url);
              },
              // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate trigger list
              [onChange]
            );
            return (
              <FieldUI.Url
                MODE="edit"
                UI={FieldUI}
                Board={Board}
                path={path}
                value={value}
                onChange={onChanged}
                {...props}
                validate
                detailed
              />
            );
          },
          {
            label: 'External URL',
            external: true,
          },
        ],
        link_text: ui`Text`({
          label: 'Featured Link Text',
        }),
      })({
        visible: when`../type`.is.equal.to('external'),
      }),
      thumbnail: ui`Upload/Image`({ label: 'Thumbnail' }),
    }),
    settings: {
      noIndex: ui`Switch`({ label: 'No Index' }),
      attributes: ui`AttributeSet`({
        query: {
          name: 'Document Attributes',
        },
        controlled: 'value',
      }),
    },
  },
  view: Document.Renderer(UI),
}));

const RendererComponent = connectLocation(
  ({ setLinkMeta, PB_LINK_META, type, external, document, dms, thumbnail: thumbnailProp }) => {
    const sourceByType = { upload: document, dms };
    const source = type in sourceByType ? sourceByType[type] : external;
    const { url, link_text, mime_type, size_bytes } = source || {};
    const thumbnail = type === 'dms' ? dms?.thumbnail : thumbnailProp;
    const heading = Board.settings.attributes?.title;
    const text = Board.settings.attributes?.short_description;
    const extension = mime.extension(mime_type);
    const analytics = {
      track: 'file-download',
      'file-name': Board.settings.attributes?.title,
      'doc-type': (Board.settings.attributes?.['content-type'] || []).join(','),
      'file-type': extension,
      'element-location': 'main section text link',
    };

    if (!_.isEqual(PB_LINK_META[url], { analytics })) {
      setLinkMeta(url, { analytics });
    }

    const mo = {
      prehead: '',
      imgSize: 'document',
      aspectRatio: '0.77',
      image: thumbnail
        ? {
            alt: Board.settings.attributes?.title,
            file: thumbnail,
          }
        : null,
      url,
      heading,
      text,
      target: '_blank',
      linkText: type === 'external' ? link_text || url : <FileBadge extension={extension} sizeBytes={size_bytes} />,
      analytics,
    };
    const docObj = (type === 'dms' ? { document: '', title: '', description: '', url: '', ...dms } : external) || {};
    return (
      <Content.PreventClicks>
        <h4>Inline Link</h4>
        <div>
          <UI.Text.Rich
            singular
            key={type}
            content={{
              ops:
                ['external', 'dms'].includes(type) || (type === 'upload' && document?.url)
                  ? [
                      {
                        insert: {
                          document: ['external', 'dms'].includes(type)
                            ? {
                                title: heading,
                                external: docObj,
                              }
                            : {
                                title: Board.settings.attributes?.title,
                                document: wrapRef('Cms::Asset', document.url.match(/\/assets\/(.*)\/file/)[1]),
                              },
                        },
                      },
                    ]
                  : [],
            }}
          />
        </div>
        <br />
        <h4>Featured</h4>
        <div>
          <MediaObject {...mo} />
        </div>
      </Content.PreventClicks>
    );
  }
);

Document.Renderer = (UI, _parameters = {}) => ({
  contents: ({ value }) => <RendererComponent {...value} />,
});

export default Document;
